'use client';

import { useState } from 'react';
import { cn } from '../../../lib/utils';
import type { ProductPageProps } from './client/product-details-types';

export type GiftCardVariant = NonNullable<
  ProductPageProps['giftCardConfiguration']
>[number];

interface ProductDetailsGiftCardSelectorProps {
  variants: GiftCardVariant[];
  selectedVariant?: GiftCardVariant | null;
  onVariantChange?: (variant: GiftCardVariant) => void;
  className?: string;
}

export function ProductDetailsGiftCardSelector({
  variants,
  selectedVariant: controlledSelected,
  onVariantChange,
  className,
}: ProductDetailsGiftCardSelectorProps) {
  const [internalSelected, setInternalSelected] =
    useState<GiftCardVariant | null>(null);

  // Use controlled value if provided, otherwise use internal state
  const selectedVariant =
    controlledSelected !== undefined ? controlledSelected : internalSelected;

  const handleVariantSelect = (variant: GiftCardVariant) => {
    if (controlledSelected === undefined) {
      setInternalSelected(variant);
    }
    onVariantChange?.(variant);
  };

  if (!variants?.length) return null;

  // Sort variants by price (ascending)
  const sortedVariants = [...variants].sort((a, b) => a.price - b.price);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <h3 className="font-semibold text-[hsl(var(--color))] text-sm uppercase">
        Gift Card Amount
      </h3>
      <fieldset className="grid grid-cols-2 gap-3 border-0 p-0 sm:grid-cols-3 lg:grid-cols-4">
        {sortedVariants.map((variant) => (
          <GiftCardOption
            key={variant.gid}
            variant={variant}
            isSelected={selectedVariant?.gid === variant.gid}
            onSelect={() => handleVariantSelect(variant)}
          />
        ))}
      </fieldset>
    </div>
  );
}

interface GiftCardOptionProps {
  variant: GiftCardVariant;
  isSelected: boolean;
  onSelect: () => void;
}

function GiftCardOption({
  variant,
  isSelected,
  onSelect,
}: GiftCardOptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex flex-col items-center justify-center rounded-theme-sm border-2 p-4 text-center transition-all duration-200 ease-in-out',
        'hf:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--color))] focus-visible:ring-offset-2',
        isSelected
          ? 'border-[hsl(var(--color))] bg-[hsl(var(--color))]/5'
          : 'border-gray-300 hf:border-[hsl(var(--color))]/50'
      )}
      aria-pressed={isSelected}
      aria-label={`Select ${variant.title} gift card`}
    >
      <div className="font-bold text-[hsl(var(--color))] text-lg">
        ${variant.price}
      </div>
      <div className="mt-1 font-medium text-[hsl(var(--color))]/80 text-sm">
        Gift Card
      </div>
    </button>
  );
}
